export type HonorPeriod = 'week' | 'month' | 'all';

export interface PeriodRange {
  startTime?: string;
  endTime?: string;
  endsAt?: Date;
}

const VN_OFFSET_MS = 7 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Kỳ theo giờ Việt Nam (UTC+7). week: thứ Hai 00:00 -> thứ Hai kế 00:00;
 * month: ngày 1 00:00 -> ngày 1 tháng sau 00:00; all: {}.
 * endTime = đầu kỳ sau - 1 ms. Chuỗi ISO UTC ("...Z"). endsAt = đầu kỳ sau.
 *
 * Tính hoàn toàn bằng cộng/trừ mili-giây UTC (Date.UTC / getUTC*) để không
 * phụ thuộc múi giờ của máy chạy: cộng 7 giờ vào mốc UTC rồi đọc các
 * trường getUTC* của kết quả chính là các trường giờ Việt Nam tương ứng.
 */
export const periodRange = (period: HonorPeriod, now: Date = new Date()): PeriodRange => {
  if (period === 'all') return {};

  const localMs = now.getTime() + VN_OFFSET_MS;
  const local = new Date(localMs);
  const y = local.getUTCFullYear();
  const m = local.getUTCMonth();
  const d = local.getUTCDate();

  let startLocalMs: number;
  let nextStartLocalMs: number;

  if (period === 'week') {
    // getUTCDay(): 0 = Chủ nhật ... 6 = thứ Bảy. Số ngày kể từ thứ Hai gần nhất.
    const dow = local.getUTCDay();
    const daysSinceMonday = (dow + 6) % 7;
    startLocalMs = Date.UTC(y, m, d - daysSinceMonday, 0, 0, 0, 0);
    nextStartLocalMs = startLocalMs + 7 * DAY_MS;
  } else {
    startLocalMs = Date.UTC(y, m, 1, 0, 0, 0, 0);
    nextStartLocalMs = Date.UTC(y, m + 1, 1, 0, 0, 0, 0);
  }

  const startUtcMs = startLocalMs - VN_OFFSET_MS;
  const nextStartUtcMs = nextStartLocalMs - VN_OFFSET_MS;

  return {
    startTime: new Date(startUtcMs).toISOString(),
    endTime: new Date(nextStartUtcMs - 1).toISOString(),
    endsAt: new Date(nextStartUtcMs),
  };
};

/** "Còn 3 ngày 5 giờ nữa chốt tuần" | "Còn 3 ngày nữa chốt tuần" (bỏ phần bằng 0) | "Còn 5 giờ nữa chốt tháng" | "Còn 8 phút nữa chốt tuần"; ms <= 0 => "Đang chốt kỳ". */
export const formatPeriodLeft = (ms: number, period: 'week' | 'month'): string => {
  if (ms <= 0) return 'Đang chốt kỳ';

  const label = period === 'week' ? 'tuần' : 'tháng';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    const hourPart = hours > 0 ? ` ${hours} giờ` : '';
    return `Còn ${days} ngày${hourPart} nữa chốt ${label}`;
  }
  if (hours > 0) {
    const minutePart = minutes > 0 ? ` ${minutes} phút` : '';
    return `Còn ${hours} giờ${minutePart} nữa chốt ${label}`;
  }
  return `Còn ${minutes} phút nữa chốt ${label}`;
};

/** "120 thích · 30 bình luận · 5 chia sẻ", bỏ phần bằng 0; tất cả 0 => "". */
export const interactionBreakdown = (x: { likes: number; comments: number; shares: number }): string => {
  const parts: string[] = [];
  if (x.likes > 0) parts.push(`${x.likes} thích`);
  if (x.comments > 0) parts.push(`${x.comments} bình luận`);
  if (x.shares > 0) parts.push(`${x.shares} chia sẻ`);
  return parts.join(' · ');
};
